import { LMList } from "../../utils/LMConstants";
import { formatShotData, getAllMeasurementsForLMInfo, isValidTaskId } from "../../utils/Utilities";
import { downloadCSV } from "../../mobile/mobileHandler";

const TEMPLATE_SELECTION_COOKIE = "lmreceiver_template_selection";
const TEMPLATE_SELECTION_MAX_AGE = 60 * 60 * 24 * 30;

const LMListOrder = [
    "TOP_TRACER",
    "GC_QUAD",
    "RESEARCH_QUAD",
    "COCOON",
    "HUNTER",
    "TRACKMAN"
];
const IMPACT_MM_COLUMNS = {
    ImpactHorizLocInch: "ImpactHorizLocMM",
    ImpactVertLocInch: "ImpactVertLocMM",
    AdjImpHorizLocInch: "AdjImpHorizLocMM",
    AdjImpVertLocInch: "AdjImpVertLocMM"
};
function getImpactMmHeader(capability) {
    return IMPACT_MM_COLUMNS[capability] || null;
}

function getImpactMmValue(capability, measuredData) {
    if (!IMPACT_MM_COLUMNS[capability] || measuredData === '' || measuredData == null) {
        return '';
    }

    return (Number(measuredData) * 25.4).toFixed(2);
}
async function loadTemplateColumns(){
    if(!window.showOpenFilePicker){
        return null;
    }

    try{
        const fileHandle = await window.showOpenFilePicker({
                                        types: [{
                                        description: "Use Template?",
                                        accept: {
                                            "text/plain": [".csv", ".txt"],
                                            
                                        }
                                        }],
                                        multiple: false,
                                        }).then(([h]) => h).catch(() => null);

        if(!fileHandle){
            return null;
        }

        const file = await fileHandle.getFile();
        const text = await file.text();

        const templateColumns = text
            .split(/\r?\n/)
            .flatMap(line => line.split(','))
            .map(column => column.trim())
            .filter(Boolean);

        if(templateColumns.length === 0){
            return null;
        }

        return { columns: templateColumns, fileName: file.name };
    }catch(error){
        console.warn('Template could not be read.', error);
        return null;
    }
}
function parseLaunchMonitorTemplate(templateColumns) {
  if (!Array.isArray(templateColumns) || templateColumns.length === 0) {
    return null;
  }

  // Known LM types and allowed capabilities (from LMList)
  const knownLMTypes = new Set(LMList.map(lm => lm.type));
  const capabilitiesByLM = new Map(
    LMList.map(lm => [lm.type, new Set(lm.capabilities)])
  );

  const selections = [];
  let currentLMType = null;

  const normalizeLMType = (s) =>
    (s || "").trim().replace(/\s+/g, "_").toUpperCase();

  for (const rawLine of templateColumns) {
    if (!rawLine) continue;

    const line = rawLine.trim();
    if (line === "") continue;

    // If the line is exactly a known LM type, switch the current LM context.
    const asLMType = normalizeLMType(line);
    if (knownLMTypes.has(asLMType)) {
      currentLMType = asLMType;
      continue;
    }

    // Otherwise treat as capability line(s) under the current LM type.
    if (!currentLMType) {
      // No LM header yet -> ignore capability line
      continue;
    }

    const allowed = capabilitiesByLM.get(currentLMType);
    if (!allowed) continue;
    const extraAllowed = currentLMType === "GC_QUAD"
        ? new Set(Object.values(IMPACT_MM_COLUMNS))
        : new Set();
  
    // Support: single capability, comma-separated list, or spaced list.
    // Your example uses commas, so we split on commas first.
    const capabilityTokens = line
      .split(",")
      .map(x => x.trim())
      .filter(Boolean);

    for (const capability of capabilityTokens) {
      if (allowed.has(capability) || extraAllowed.has(capability)) {
            selections.push({ lmType: currentLMType, capability });
        }

    }
  }
  return selections.length > 0 ? selections : null;
}

export function Save(func, inApp, fileName = ""){
    if(!inApp || (inApp && !window.webkit)){
        const suggestedFileName = fileName ? fileName : "data";
        
        let userFileName = window.prompt('Outputted CSV filename: ', suggestedFileName);
        if(userFileName === null){
            return;
        }
        userFileName += ".csv";
        const csvText = func;

        const blob = new Blob([csvText], { type: 'text/csv' });
    
        // Create a download link
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = userFileName; // Filename provided by the user
    
        // Append the link to the body and trigger the download
        document.body.appendChild(link);
        link.click();
    
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }else{
        const csvText = func;
        const chunkSize = 100; // Adjust chunk size if needed
        const totalChunks = Math.ceil(csvText.length / chunkSize);
    
        for (let i = 0; i < totalChunks; i++) {
            const chunk = csvText.slice(i * chunkSize, (i + 1) * chunkSize);
            downloadCSV(chunk, i === totalChunks - 1);
        }
    }
}
export async function GetCSVTemplate(session) {
    const templateResult = await loadTemplateColumns();
    if(!templateResult){
        return null;
    }

    const { columns, fileName } = templateResult;
    const templateSelection = parseLaunchMonitorTemplate(columns);
    if(!templateSelection){
        return null;
    }

    const savedAt = new Date().toISOString();
    saveTemplateSelectionCookie(templateSelection, savedAt, fileName);

    return { templateSelection, savedAt, fileName };
}

function saveTemplateSelectionCookie(templateSelection, savedAt, fileName) {
  const payload = JSON.stringify({ templateSelection, savedAt, fileName });
  localStorage.setItem(TEMPLATE_SELECTION_COOKIE, payload);
}

function readTemplateSelectionCookie() {
  const raw = localStorage.getItem(TEMPLATE_SELECTION_COOKIE);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn("Template storage could not be parsed.", error);
    return null;
  }
}

export function getTemplateSelectionCookieData() {
  const data = readTemplateSelectionCookie();

  if (!data || !Array.isArray(data.templateSelection) || data.templateSelection.length === 0) {
    return null;
  }

  return data;
}

export function getTemplateSelectionFromCookie() {
  const data = getTemplateSelectionCookieData();
  return data ? data.templateSelection : null;
}

export function clearTemplateSelectionCookie() {
  localStorage.removeItem(TEMPLATE_SELECTION_COOKIE);
}

export async function GenerateCSVSingle(session,templateSelection){
    let csvData = {text: ""};

    CSVSerialize_SingleSession(csvData, session);

    if(session.SessionSetups && session.SessionSetups.length > 0){
        CSVAddSpacerRow(csvData);

        CSVSerialize_LMHeader(csvData, session, templateSelection);

        let needHeader = true;

        for(let i = 0; i < session.SessionSetups.length; i++){
            const setup = session.SessionSetups[i];

            if(setup.Shots && setup.Shots.length > 0){
                if(needHeader){
                    CSVSerialize_ShotHeader(csvData, session, templateSelection);
                    needHeader = false;
                }

                for(let j = 0; j < setup.Shots.length; j++){
                    const shot = setup.Shots[j];
                    CSVSerialize_Shot(csvData, session, setup, shot, templateSelection);
                }
            }
        }
    }

    return csvData.text;
}

export async function GenerateCSVMultiple(sessions,templateSelection){
    let csvData = {text: ""};

    CSVSerialize_MultipleSession(csvData, sessions);

    let isFirstSession = true;
   // const templateColumns = await loadTemplateColumns();
   // const templateSelection = parseLaunchMonitorTemplate(templateColumns);

    for(let i = 0; i < sessions.length; i++){
        const session = sessions[i];

        if(session.SessionSetups && session.SessionSetups.length > 0){
            if(isFirstSession){
                CSVAddSpacerRow(csvData);
                CSVSerialize_LMHeader(csvData, session, templateSelection);
                CSVSerialize_ShotHeader(csvData,session, templateSelection);
                isFirstSession = false;
            }
            
            for(let j = 0; j < session.SessionSetups.length; j++){
                const setup = session.SessionSetups[j];

                if(setup.Shots && setup.Shots.length > 0){
                    
                    for(let k = 0; k < setup.Shots.length; k++){
                        const shot = setup.Shots[k];

                        CSVSerialize_Shot(csvData,session,setup,shot, templateSelection);
                    }
                }
            }
        }
    }

    return csvData.text;
}

//CSV Session Utility
function CSVSerialize_SingleSession(dataWrapper, session){
    CSVAddRow(dataWrapper, ["Session ID", session.id]);
    CSVAddRow(dataWrapper, ["Taskman TaskID", isValidTaskId(session.TaskID) ? session.TaskID : '']);
    CSVAddRow(dataWrapper, ["Description", session.Description]);
    CSVAddRow(dataWrapper, ["Data Source", session.DataSource]);
    CSVAddRow(dataWrapper, ["Data Collector Email", session.DataCollectorId]);
    CSVAddRow(dataWrapper, ["Data Collector Name", session.DataCollectorName]);
    CSVAddRow(dataWrapper, ["Golfer Email", session.UserId]);
    CSVAddRow(dataWrapper, ["Golfer Name", session.UserName]);
    CSVAddRow(dataWrapper, ["Venue ID", session.VenueId ? session.VenueId : '']);
    CSVAddRow(dataWrapper, ["Venue", session.Name ? session.Name : '']);
    CSVAddRow(dataWrapper, ["Date", ConvertISOToLocalTime(session.Date)]);
    CSVAddRow(dataWrapper, ["Pressure (in MmHg)", session.PressureMmHg ? session.PressureMmHg : '']);
    CSVAddRow(dataWrapper, ["Temperature (F)", session.TemperatureF ? session.TemperatureF : '']);
    CSVAddRow(dataWrapper, ["Wind Direction", session.WindDirection ? session.WindDirection : '']);
    CSVAddRow(dataWrapper, ["Wind Speed (MPH)", session.WindSpeedMph ? session.WindSpeedMph : '']);
}

function CSVSerialize_MultipleSession(dataWrapper, sessions){

    if(sessions.length === 0){
        return;
    }

    const initSession = sessions[0];
    CSVAddRow(dataWrapper, ["Venue", initSession.VenueName]);
    CSVAddRow(dataWrapper, ["Taskman TaskID", isValidTaskId(initSession.TaskID) ? initSession.TaskID : '' ]);
    CSVAddRow(dataWrapper, ["Description", initSession.Description]);

    CSVAddSpacerRow(dataWrapper);

    for(let i = 0; i < sessions.length; i++) {
        const session = sessions[i];

        // Ignore blank sessions or sessions with no shots
        if (!session.SessionSetups || session.SessionSetups.length === 0) {
            continue;
        }

        let setupsWithShots = 0;

        for (let j = 0; j < session.SessionSetups.length; j++) {
            const setup = session.SessionSetups[j];
            if (setup && setup.Shots && setup.Shots.length > 0) {
                setupsWithShots++;
            }
        }

        if (setupsWithShots === 0) {
            continue;
        }

        CSVAddRow(dataWrapper, ["Session ID", session.id]);
        CSVAddRow(dataWrapper, ["Golfer Name", session.UserName]);
        CSVAddSpacerRow(dataWrapper);
    }
}

function CSVSerialize_Setup(dataWrapper, setup){
    CSVAddRow(dataWrapper, ["Description", setup.Id]);
    CSVAddRow(dataWrapper, ["Club", setup.ClubName]);

    //TODO: Adjustments...

    CSVAddRow(dataWrapper, ["Shaft", setup.ShaftModel]);
    CSVAddRow(dataWrapper, ["Ball", setup.BallName]);
}

function buildLMHeaderRow(session, templateSelection = null){
    let headers = [];
    headers.push('','','','','','');//padding
    if(SessionHasBallSeries(session)){
        headers.push('');//extra padding
    }
    headers.push('','','','','');//padding

    if(templateSelection && templateSelection.length > 0){
        const lmOrder = [];
        const capabilityCounts = new Map();

        templateSelection.forEach(selection => {
            if(!lmOrder.includes(selection.lmType)){
                lmOrder.push(selection.lmType);
            }

            capabilityCounts.set(selection.lmType, (capabilityCounts.get(selection.lmType) || 0) + 1);
        });

        lmOrder.forEach(lmType => {
            const count = capabilityCounts.get(lmType) || 0;

            if(count > 0){
                headers.push(lmType);

                for(let i = 1; i < count; i++){
                    headers.push('');
                }
            }
        });

        return headers;
    }

    for(let i = 0; i < LMListOrder.length; i++){
    const LM = LMListOrder[i];
    headers.push(LM);

    LMList.filter(item => item.type === LM).forEach(lmItem => {
        let extraColumns = 0;

        if (LM === "GC_QUAD") {
            lmItem.capabilities.forEach(capability => {
                if (getImpactMmHeader(capability)) {
                    extraColumns++;
                }
            });
        }

        const totalColumnsForLm = lmItem.capabilities.length + extraColumns;

        for(let j = 1; j < totalColumnsForLm; j++){
            headers.push('');
        }
    });
}


    return headers;
}

function buildShotHeaderRow(session, templateSelection = null){
    let headers = [];
    headers.push("Golfer");
    headers.push("Description");
    headers.push("Club");
    headers.push("Loft");
    headers.push("Shaft");
    headers.push("Ball");

    if(SessionHasBallSeries(session)){
        headers.push("Group");
    }

    headers.push("Shot#");
    headers.push("Shot Id");
    headers.push("DateTime");
    headers.push("Shot Selected");
    headers.push("ClubPrototypeID");

    if(templateSelection && templateSelection.length > 0){
        templateSelection.forEach(selection => headers.push(selection.capability));

        return headers;
    }

    for(let i = 0; i < LMListOrder.length; i++){
    const LM = LMListOrder[i];
    LMList.filter(item => item.type === LM).forEach(lmItem => {
        lmItem.capabilities.forEach(capability => {
            headers.push(capability);

            if (LM === "GC_QUAD") {
                const mmHeader = getImpactMmHeader(capability);
                if (mmHeader) {
                    headers.push(mmHeader);
                    }
                }
            });
        });
    }


    return headers;
}

function buildShotDataRow(session, setup, shot, templateSelection = null){
    let shotData = [];

    if(session.TaskID){
        shotData.push(session.UserName);
    }else{
        shotData.push('');
    }

    shotData.push(GetTruncatedDisplayName(setup));
    shotData.push(setup.ClubName === "Prototype" ?
        (setup.ClubPrototypeId ? setup.ClubPrototypeId : setup.ClubName) : setup.ClubName);
    shotData.push(setup.ClubLoft ? setup.ClubLoft : '');
    shotData.push(setup.ShaftModel);
    shotData.push(setup.BallName === "Prototype" ?
        (setup.BallPrototypeId ? setup.BallPrototypeId : setup.BallName) : setup.BallName);

    if(SessionHasBallSeries(session)){
        shotData.push(shot.Group);
    }

    shotData.push(shot.ShotNumber);

    const firstValidShotId  = shot.LMInfos.find(info => info.ShotId && info.ShotId.trim() !== "");
    shotData.push(firstValidShotId && firstValidShotId.ShotId ? firstValidShotId.ShotId : '');

    if(shot.TimeStamp){
        shotData.push(ConvertISOToLocalTime(shot.TimeStamp));
    }else{
        shotData.push('');
    }

    shotData.push(shot.IsChecked ? "YES": "NO");

    if(!setup){
        shotData.push('');
    }else if(!setup.ClubPrototypeId){
        shotData.push('');
    }else{
        shotData.push(setup.ClubPrototypeId);
    }

    if(templateSelection && templateSelection.length > 0){
        templateSelection.forEach(selection => {
            const lmInfo = shot.LMInfos.find(info => info.LMType === selection.lmType);
            const measurements = getAllMeasurementsForLMInfo(lmInfo);

            const isCsvOnlyMm = Object.values(IMPACT_MM_COLUMNS).includes(selection.capability);

            if (isCsvOnlyMm) {
                const sourceCapability = Object.keys(IMPACT_MM_COLUMNS).find(
                    key => IMPACT_MM_COLUMNS[key] === selection.capability
                );
                const sourceValue = sourceCapability ? measurements[sourceCapability] ?? '' : '';
                shotData.push(getImpactMmValue(sourceCapability, sourceValue));
            } else {
                const measuredData = measurements[selection.capability] ?? '';
                if(measuredData !== ''){
                    shotData.push(formatShotData(selection.capability, measuredData, true));
                }else{
                    shotData.push('');
                }
            }
        });

        return shotData;
    }

    for(let i = 0; i < LMListOrder.length; i++){
        const LM = LMListOrder[i];
        const lmInfo = shot.LMInfos.find(info => info.LMType === LM);

        LMList.filter(item => item.type === LM).forEach(lmItem => {
            lmItem.capabilities.forEach(capability => {
                if(lmInfo){
                    const measurements = getAllMeasurementsForLMInfo(lmInfo);
                    const measuredData = measurements[capability] ?? '';

                    if(measuredData !== ''){
                        shotData.push(formatShotData(capability, measuredData, true));
                    }else{
                        shotData.push('');
                    }

                    if (LM === "GC_QUAD" && getImpactMmHeader(capability)) {
                        shotData.push(getImpactMmValue(capability, measuredData));
                    }
                }else{
                    shotData.push('');

                    if (LM === "GC_QUAD" && getImpactMmHeader(capability)) {
                        shotData.push('');
                    }
                }
            });
        });
    }

    return shotData;
}



function CSVSerialize_LMHeader(dataWrapper, session, templateSelection = null){
    CSVAddRow(dataWrapper, buildLMHeaderRow(session, templateSelection));
}

function CSVSerialize_ShotHeader(dataWrapper, session, templateSelection = null){
    const headers = buildShotHeaderRow(session, templateSelection);

    CSVAddRow(dataWrapper, headers);

    return headers.length;
}

function CSVSerialize_Shot(dataWrapper, session, setup, shot, templateSelection = null){
    CSVAddRow(dataWrapper, buildShotDataRow(session, setup, shot, templateSelection));
}

//CSV Utility
function CSVAddSpacerRow(dataWrapper, fill = "", columns = 1){
    let fillItems = []; // Initialize an empty array

    if (columns < 1) {
        columns = 1;
    }

    for (let i = 0; i < columns; i++) {
        let fillFinal = "";

        if (fill) { // Check if `fill` is not empty or null
            for (let j = 0; j < 25; j++) {
                fillFinal += fill;
            }
        }

        fillItems.push(fillFinal);
    }
    CSVAddRow(dataWrapper, fillItems);
}

function CSVAddRow(dataWrapper, columnValues) {
    let dataToWrite = "";

    for (let i = 0; i < columnValues.length; i++) {
        dataToWrite += columnValues[i];

        if (i !== columnValues.length - 1) {
            dataToWrite += ",";
        }
    }

    dataWrapper.text += dataToWrite + "\n";
}

//Utility
function SessionHasBallSeries(session){
    return session.BallSeries && session.BallSeries.length > 0;
}

function ConvertISOToLocalTime(time){
    const date = new Date(time);

    return date.toLocaleString().replace(',','');
}

function GetTruncatedDisplayName(setup){
    // Clubname
    let clubString = "";
    if (setup.ClubName === "Prototype"){
        clubString = (setup.LetterId ? `(${setup.LetterId}) ` : "") + (setup.ClubDescription ? setup.ClubDescription : setup.ClubName) + (setup.ClubId ? `[${setup.ClubId}]` : "");
    } else if(setup.ClubName === "Custom") {
        clubString = (setup.LetterId ? `(${setup.LetterId}) ` : "") + setup.ClubName + (setup.ClubId ? ` [${setup.ClubId}]` : "");
    } else {
        clubString = (setup.LetterId ? `(${setup.LetterId}) ` : "") + setup.ClubName;
    }

    // Ballname
    let ballString = "";
    if (setup.BallName === "Prototype") {
        ballString = setup.BallName + (setup.BallPrototypeId ? ` [${setup.BallPrototypeId}]` : "");
    } else {
        ballString = setup.BallName;
    }

    // Return the formatted string
    return `Club:(${clubString}) Ball:(${ballString})`;
}
